import { MicroLabel } from "@/components/ui/MicroLabel";
import { ChoiceGroup, stepRadio } from "@/components/ui/ChoiceGroup";
import { SettingsPanel } from "@/components/ui/SettingsPanel";
import type {
  ConnectorDefaults,
  FigureSettings,
  NumberingConvention,
  NumeralStyle,
} from "@/lib/element-registry";
import { CONVENTION_CHOICES } from "@/lib/numbering";

const PANEL_TITLE = "Figure Settings";

/**
 * The little drawing each style is shown against.
 *
 * A part with two numerals leading to it, rather than two numerals on their own: the choice
 * is about how a numeral sits on artwork, and floating digits show none of that.
 */
const PART = { x: 44, y: 28, width: 18, height: 12 };
const MARKS = [
  { at: { x: 18, y: 9 }, tip: { x: 48, y: 30 }, numeral: "12" },
  { at: { x: 88, y: 9 }, tip: { x: 58, y: 32 }, numeral: "14" },
];
const RING = 8;

/**
 * How far the leader starts from the numeral it names, under each style.
 *
 * The ring's own radius when there is a ring, so the line begins on the circle rather than
 * inside it and no leader crosses the numeral it belongs to. A shade less without one, which
 * clears the digits and no more.
 */
const LEADER_CLEARS: Record<NumeralStyle, number> = {
  circled: RING,
  plain: 7,
};

/** Two numbered leaders reaching a part, drawn the way this style draws them. */
function StylePreview({ style }: { style: NumeralStyle }) {
  const clear = LEADER_CLEARS[style];

  return (
    <svg
      role="img"
      aria-hidden
      viewBox="0 0 104 46"
      className="h-[46px] w-full"
    >
      <rect
        x={PART.x}
        y={PART.y}
        width={PART.width}
        height={PART.height}
        fill="none"
        className="stroke-foreground"
        strokeWidth={1}
      />
      {MARKS.map((mark) => {
        const by = { x: mark.tip.x - mark.at.x, y: mark.tip.y - mark.at.y };
        const run = Math.hypot(by.x, by.y);
        return (
          <g key={mark.numeral}>
            <line
              x1={mark.at.x + (by.x / run) * clear}
              y1={mark.at.y + (by.y / run) * clear}
              x2={mark.tip.x}
              y2={mark.tip.y}
              className="stroke-foreground"
              strokeWidth={1}
            />
            {style === "circled" && (
              <circle
                cx={mark.at.x}
                cy={mark.at.y}
                r={RING}
                className="fill-card stroke-foreground"
                strokeWidth={1.5}
              />
            )}
            <text
              x={mark.at.x}
              y={mark.at.y + 3.5}
              textAnchor="middle"
              fontSize={style === "circled" ? 8 : 11}
              fontWeight={700}
              className="fill-foreground"
              fontFamily={
                style === "circled"
                  ? "inherit"
                  : "Times New Roman, Times, serif"
              }
            >
              {mark.numeral}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** The numeral styles, each shown as a picture of itself rather than only named. */
function NumeralStyles({
  chosen,
  onChoose,
}: {
  chosen: NumeralStyle;
  onChoose: (style: NumeralStyle) => void;
}) {
  const styles: { label: string; value: NumeralStyle }[] = [
    { label: "Plain", value: "plain" },
    { label: "Circled", value: "circled" },
  ];
  return (
    <div className="mt-3">
      <MicroLabel>Default numeral style</MicroLabel>
      <div
        role="radiogroup"
        aria-label="Numeral style"
        className="mt-1.5 grid grid-cols-2 gap-2"
      >
        {styles.map((style, index) => (
          <button
            key={style.value}
            type="button"
            role="radio"
            aria-label={style.label}
            aria-checked={style.value === chosen}
            tabIndex={style.value === chosen ? 0 : -1}
            onKeyDown={(event) => {
              const to = stepRadio(event, index, styles.length);
              if (to !== undefined) onChoose(styles[to].value);
            }}
            onClick={() => {
              onChoose(style.value);
            }}
            // Filled as well as bordered when it is the chosen one. Every other group here
            // marks its choice by filling the control, and a border colour alone is the
            // weakest signal on the panel: at this size a purple hairline and a grey one
            // are the same picture.
            className={`rounded-md border-2 p-1.5 ${style.value === chosen ? "border-primary bg-muted" : "border-border bg-card"}`}
          >
            <StylePreview style={style.value} />
            <span
              className={`mt-1 block text-[11px] font-semibold ${style.value === chosen ? "text-primary" : ""}`}
            >
              {style.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Every convention the panel offers, in the order the attorney meets them.
 *
 * Exported for the dialog that shows them on first entry, so the two surfaces cannot
 * offer different conventions or the same one in a different order.
 */
export function SettingsGroups({
  settings,
  onConnectorDefaults,
  onNumeralStyle,
  onNumberingConvention,
}: {
  settings: FigureSettings;
  onConnectorDefaults: (defaults: ConnectorDefaults) => void;
  onNumeralStyle: (style: NumeralStyle) => void;
  onNumberingConvention: (convention: NumberingConvention) => void;
}) {
  const line = settings.connector_defaults;
  // Named once rather than at each group, so a choice states the whole convention and no
  // group can send a partial one.
  const changed = (over: Partial<ConnectorDefaults>) => {
    onConnectorDefaults({ ...line, ...over });
  };

  // A straight default has no anchors, and the group that sets them is hidden under one. A
  // count left standing behind it would still govern the toolbar's Curved button, minting an
  // S from a setting the panel no longer shows.
  const shaped = (shape: ConnectorDefaults["shape"]) => {
    changed(shape === "straight" ? { shape, anchors: 1 } : { shape });
  };

  return (
    <div className="mt-2.5">
      <ChoiceGroup
        label="Default connector shape"
        chosen={line.shape}
        choices={[
          { label: "Straight", value: "straight" as const },
          { label: "Curved", value: "curved" as const },
        ]}
        onChoose={shaped}
      />

      {line.shape === "curved" && (
        <>
          <ChoiceGroup
            label="Default anchor points"
            chosen={line.anchors}
            choices={[
              { label: "One", value: 1 as const },
              { label: "Two", value: 2 as const },
            ]}
            onChoose={(anchors) => {
              changed({ anchors });
            }}
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Two anchor points allow S-shaped connectors.
          </p>
        </>
      )}

      <ChoiceGroup
        label="Arrowhead"
        chosen={line.arrowhead}
        choices={[
          { label: "Arrowhead", value: true as const },
          { label: "No arrowhead", value: false as const },
        ]}
        onChoose={(arrowhead) => {
          changed({ arrowhead });
        }}
      />

      <NumeralStyles
        chosen={settings.numeral_style}
        onChoose={onNumeralStyle}
      />

      <p className="mt-2.5 text-[11px] text-muted-foreground">
        Marks already drawn are left as they are. Change one of them from the
        toolbar beside the drawing.
      </p>

      <ChoiceGroup
        label="Numbering convention"
        chosen={settings.numbering_convention}
        choices={[...CONVENTION_CHOICES]}
        onChoose={onNumberingConvention}
        stacked
      />
    </div>
  );
}

/**
 * The conventions the whole set is drawn under, and the gear that opens them.
 *
 * Everything the panel shows is read from the settings it is given on every render, so a
 * reconcile, or another screen editing the same task, shows through here.
 */
export function FigureSettingsMenu({
  settings,
  onConnectorDefaults,
  onNumeralStyle,
  onNumberingConvention,
  align,
}: {
  settings: FigureSettings;
  onConnectorDefaults: (defaults: ConnectorDefaults) => void;
  onNumeralStyle: (style: NumeralStyle) => void;
  onNumberingConvention: (convention: NumberingConvention) => void;
  /** Which edge of the button the panel lines up with; see SettingsPanel. */
  align?: "start" | "end";
}) {
  return (
    <SettingsPanel
      align={align}
      title={PANEL_TITLE}
      note="Defaults for every figure"
      closeLabel="Close figure settings"
    >
      <SettingsGroups
        settings={settings}
        onConnectorDefaults={onConnectorDefaults}
        onNumeralStyle={onNumeralStyle}
        onNumberingConvention={onNumberingConvention}
      />
    </SettingsPanel>
  );
}
